import { ImageDecodeError, ImageTooLargeError, UnsupportedFormatError } from '@/core/image/errors'
import {
  detectImageFormat,
  FORMAT_SUPPORT,
  FormatSupportKind,
  ImageFormat,
  usesFirstFrame,
} from '@/core/image/formats'
import {
  PREVIEW_MAX_DIMENSION,
  reportedDeviceMemory,
  sourcePixelLimit,
} from '@/core/image/limits'
import { SourceImageNotice, type SourceImage } from '@/core/image/SourceImage'
import { readExifDate } from '@/core/image/readExifDate'

const IMAGE_HEADER_BYTES = 64
const UNKNOWN_IMAGE_MIME_TYPE = 'application/octet-stream'
const HEIC_CONVERSION_MIME_TYPE = 'image/jpeg'
const HEIC_CONVERSION_QUALITY = 1

function previewResizeOptions(width: number, height: number): ImageBitmapOptions {
  if (width >= height) {
    return { resizeWidth: Math.min(width, PREVIEW_MAX_DIMENSION), resizeQuality: 'high' }
  }
  return { resizeHeight: Math.min(height, PREVIEW_MAX_DIMENSION), resizeQuality: 'high' }
}

function validateHeicConversion(converted: Blob): void {
  if (converted.size <= 0 || converted.type !== HEIC_CONVERSION_MIME_TYPE) {
    throw new Error(
      `HEIC conversion must return a non-empty ${HEIC_CONVERSION_MIME_TYPE} blob; `
      + `received type "${converted.type}" with ${converted.size} bytes.`,
    )
  }
}

export async function loadSource(file: File): Promise<SourceImage> {
  const header = new Uint8Array(await file.slice(0, IMAGE_HEADER_BYTES).arrayBuffer())
  const format = detectImageFormat(header)
  if (format && FORMAT_SUPPORT[format].kind === FormatSupportKind.Unsupported) {
    throw new UnsupportedFormatError(format)
  }

  const capturedDatePromise = readExifDate(file)
  const bitmap = await decodeToBitmap(file, format)

  const pixelCount = bitmap.width * bitmap.height
  if (bitmap.width <= 0 || bitmap.height <= 0) {
    bitmap.close()
    throw new ImageDecodeError()
  }
  const pixelLimit = sourcePixelLimit(reportedDeviceMemory(navigator))
  if (pixelCount > pixelLimit.maximumPixels) {
    bitmap.close()
    throw new ImageTooLargeError(pixelCount, pixelLimit)
  }

  let previewBitmap: ImageBitmap
  try {
    previewBitmap = await createImageBitmap(bitmap, previewResizeOptions(bitmap.width, bitmap.height))
  } catch (error) {
    bitmap.close()
    throw new ImageDecodeError(error)
  }

  return {
    file,
    bitmap,
    previewBitmap,
    width: bitmap.width,
    height: bitmap.height,
    fileName: file.name,
    mimeType: format ? FORMAT_SUPPORT[format].mimeType : UNKNOWN_IMAGE_MIME_TYPE,
    capturedDate: await capturedDatePromise,
    notice: usesFirstFrame(format, header) ? SourceImageNotice.FirstFrame : null,
  }
}

export async function decodeToBitmap(file: File, format: ImageFormat | null): Promise<ImageBitmap> {
  if (format && FORMAT_SUPPORT[format].kind === FormatSupportKind.Unsupported) {
    throw new UnsupportedFormatError(format)
  }
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch (nativeError) {
    if (format !== ImageFormat.Heic) throw new ImageDecodeError(nativeError)

    try {
      const { heicTo } = await import('heic-to/csp')
      const converted = await heicTo({
        blob: file,
        type: HEIC_CONVERSION_MIME_TYPE,
        quality: HEIC_CONVERSION_QUALITY,
      })
      validateHeicConversion(converted)
      return await createImageBitmap(converted, { imageOrientation: 'from-image' })
    } catch (wasmError) {
      throw new ImageDecodeError(new AggregateError(
        [nativeError, wasmError],
        'Native and WASM HEIC decoding both failed.',
      ))
    }
  }
}
